import events from "_src/engine/events";
import output from "_src/engine/output";
import minmaxUtils from "_src/engine/utility/minmax";
import {cameraType} from "_src/engine/constants";
import {BufferReaderType} from "_src/client/buffer_reader";
import compat from "_src/engine/compat";
import {vec3, vec4} from "_src/libs/gl-matrix";
import workspace from "_src/workspace";
import selector from "_src/manipulators/libs/selector";
import ResolvePass from "_src/render_graph/passes/resolve_pass";
import renderGraph, {RenderGraph} from "_src/render_graph/render_graph";
import renderGraphManager from "_src/render_graph/render_graph";
import GPUBufferReader from "_src/client/buffer_reader";
import {screenUtils} from "_src/client/utils";
import appState from "_src/app_state";
import config from "_src/config";
import controlFactory from "_src/client/controls/control_factory";
import TrackballControl from "_src/client/controls/trackball";
import renderer from "_src/engine/renderer";
import {objectType} from "_src/constants";
import TargetControl from "_src/client/controls/target_control";

const MAIN   = 0;
let ALIGNEE  = -1;
let ALIGNER  = -1;

const _windows = new Map();
const _GPUTimeMeasurements = [];
let _windowID = 0;
let _resolvePass = null;

let _prevWidth = 1;
let _prevHeight = 1;

class Window {

  constructor(viewport, camType = cameraType.PERSPECTIVE) {
    this.objects = [];
    this.id = _windowID;
    this._viewport = vec4.create();
    this.renderGraph = new RenderGraph(this.id === MAIN, camType);

    this.bufferReader = new GPUBufferReader(this.renderGraph);

    this._active = false;

    this.setViewport(viewport);

    const workspaceScene = this.renderGraph.getWorkspaceScene();
    const workspaceCamera = workspaceScene.camera;
    const controlType = appState.getStateValue("cameraControl");
    this.setCameraControl(controlFactory.createControl(controlType, workspaceCamera));
    this.cameraControl.setEnabled(appState.getStateValue("enableCameraControls"));
    this.cameraControl.setAngles(Math.PI / 4, Math.PI / 4);
    this.cameraControl.setDefaultPosition([-1,-1,-1,1,1,1]);

    this._initClosures();
    this._initEvents();

  }

  setCameraControl(control) {
    if (this.cameraControl instanceof TrackballControl) {
      this.cameraControl.unregisterRotationIndicator(this.renderGraph.getInterfaceScene());
    }
    this.cameraControl = control;
    if (control instanceof TrackballControl) {
      control.registerRotationIndicator(this.renderGraph.getInterfaceScene());
    }
  }

  setViewport(viewport) {
    viewport[0] = Math.round(viewport[0]);
    viewport[1] = Math.round(viewport[1]);
    viewport[2] = Math.round(viewport[2]);
    viewport[3] = Math.round(viewport[3]);

    this.renderGraph.resize(viewport[2], viewport[3]);
    vec4.copy(this._viewport, viewport);
  }

  toViewportX(windowX) {
    return windowX - this._viewport[0];
  }

  toViewportY(windowY) {
    return windowY - this._viewport[1];
  }

  getViewport() {
    return this._viewport;
  }

  addObject(object) {
    if (this.objects.indexOf(object) === -1) {
      this.objects.push(object);
    }
  }

  removeObject(object) {
    const index = this.objects.indexOf(object);
    if (index !== -1) {
      this.objects.splice(index, 1);
    }

    for (const scene of [this.renderGraph.getWorkspaceScene(), this.renderGraph.getSurfelsDepthScene(),
      this.renderGraph.getSurfelsAdditiveScene(), this.renderGraph.getPostBlendingScene()]) {
      scene.remove(object.model);
    }

    if (object.edgesModel) this.renderGraph.getInterfaceScene().remove(object.edgesModel);
  }

  updateObjectsScene() {
    const workspaceScene = this.renderGraph.getWorkspaceScene();
    const interfaceScene = this.renderGraph.getInterfaceScene();
    const surfelsDepthScene = this.renderGraph.getSurfelsDepthScene();
    const surfelsAdditiveScene = this.renderGraph.getSurfelsAdditiveScene();
    const postBlendingScene = this.renderGraph.getPostBlendingScene();

    const xRayEnabled = appState.getStateValue("colorMode") === appState.CM_XRAY;
    for (const object of this.objects) {
      if (!(object.type === objectType.SURFELS || object.type === objectType.FUSION || object.type === objectType.SCAN ||
        object.type === objectType.CAD)) {
        continue;
      }

      if (!xRayEnabled && (object.model.getOpacity() !== 1.0 || object.type === objectType.CAD && object.isPrimitive())) {
        workspaceScene.remove(object.model);
        surfelsDepthScene.remove(object.model);
        surfelsAdditiveScene.remove(object.model);
        postBlendingScene.add(object.model);
      } else {
        if (object.type === objectType.SURFELS) {
          if (object.model.shader.getSurfelsFiltering() === appState.SM_BLEND) {
            workspaceScene.remove(object.model);
            postBlendingScene.remove(object.model);
            surfelsDepthScene.add(object.model);
            surfelsAdditiveScene.add(object.model);
          } else {
            workspaceScene.add(object.model);
            postBlendingScene.remove(object.model);
            surfelsDepthScene.remove(object.model);
            surfelsAdditiveScene.remove(object.model);
          }
        } else {
          workspaceScene.add(object.model);
          postBlendingScene.remove(object.model);
        }
      }

      if (object.edgesModel) interfaceScene.add(object.edgesModel);
    }

    const surfelsBlendingEnabled = Boolean(surfelsDepthScene.getChildren().length || surfelsAdditiveScene.getChildren().length);
    const postBlendingEnabled = Boolean(postBlendingScene.getChildren().length);

    this.renderGraph.setSurfelsBlendingEnabled(surfelsBlendingEnabled);
    this.renderGraph.setPostBlendingEnabled(postBlendingEnabled);
  }

  _initClosures() {
    const _vec3Tmp = vec3.create();
    this._zoomHandler = ({screenX, screenY, delta}) => {
      if (!this.isInViewport(screenX, screenY)) {
        return;
      }

      const {x, y} = screenUtils.toScreen(this._viewport, this.renderGraph.getWorkspaceScene().camera.viewport, screenX, screenY);
      this.cameraControl.zoom(delta, x, y);
      this.renderGraph.requestFrame();
    };

    this._zoomEndHandler = ({screenX, screenY})  => {
      if (!this.isInViewport(screenX, screenY)) {
        return;
      }

      this.bufferReader.dropBuffers();
      events.emit("camera_zoom_end", this.cameraControl.object);
    }

    this._controlStartHandler = ({screenX, screenY}) => {
      if (!this.isInViewport(screenX, screenY)) {
        return;
      }

      this._setActive(true);
      const camera = this.renderGraph.getWorkspaceScene().camera;
      const {x, y} = screenUtils.toScreen(this.getViewport(), camera.viewport, screenX, screenY);
      this.cameraControl.startRotation(x, y);

      events.emit("control_move_start");
    };

    this._controlRotateHandler = ({movementX, movementY}) => {
      if (!this._isActive()) {
        return;
      }

      const camera = this.renderGraph.getWorkspaceScene().camera;
      const viewport = camera.viewport;

      const dx = movementX / viewport[2];
      const dy = movementY / viewport[3];

      this.cameraControl.rotate(dx, dy);

      this.renderGraph.requestFrame();
    };

    this._controlPanHandler = ({movementX, movementY}) => {
      if (!this._isActive()) {
        return;
      }

      const camera = this.renderGraph.getWorkspaceScene().camera;
      const viewport = camera.viewport;

      const aspect = viewport[2] / viewport[3];
      let dx = movementX / viewport[2];
      let dy = movementY / viewport[3];

      dx /= Math.max(1.0 / aspect, 1.0);
      dy /= Math.max(aspect, 1.0);

      this.cameraControl.pan(dx, dy);

      this.renderGraph.requestFrame();
    };

    this._controlEndHandler = () => {
      if (!this._isActive()) {
        return;
      }

      this.cameraControl.endRotation();
      this.bufferReader.dropBuffers();
      events.emit("control_move_end", this.cameraControl.object);
      this._setActive(false);

      this.renderGraph.requestFrame();
    };

    this._controlResetPointHandler = ({screenX, screenY}) => {
      const control = this.cameraControl;
      if (!(control instanceof TargetControl)) {
        return;
      }

      const xRayEnabled = appState.getStateValue("colorMode") === appState.CM_XRAY;
      if (xRayEnabled) {
        return;
      }

      if (!this.isInViewport(screenX, screenY)) {
        return;
      }

      const camera = this.renderGraph.getWorkspaceScene().camera;
      const point = _vec3Tmp;
      this.getPixelWorldPoint(screenX, screenY, camera, point).then(res => {
        if (res) {
          control.setTarget(point, () => {
              this.renderGraph.requestFrame();
            },
            () => {
              this.bufferReader.dropBuffers();
              this.renderGraph.requestFrame();
            });
          control.setTargetVisibility(true);

          this.bufferReader.dropBuffers();
          this.renderGraph.requestFrame();
        }
      });
    };

    this._mouseMoveHandler = ({screenX, screenY}) => {
      if (this.isInViewport(screenX, screenY)) {
        appState.setStateValue("hoverWindowOn", this.id);
      }
    };

    const worldMinmax = new Array(6);
    this._updateNearFar = () => {
      const workspaceScene = this.renderGraph.getWorkspaceScene();
      const photosScene = this.renderGraph.getPhotosScene();
      const surfelsScene   = this.renderGraph.getSurfelsDepthScene();
      const postBlendingScene = this.renderGraph.getPostBlendingScene();

      minmaxUtils.reset(worldMinmax);

      minmaxUtils.expandMinmaxByScene(worldMinmax, workspaceScene);
      minmaxUtils.expandMinmaxByScene(worldMinmax, photosScene);
      minmaxUtils.expandMinmaxByScene(worldMinmax, surfelsScene);
      minmaxUtils.expandMinmaxByScene(worldMinmax, postBlendingScene);

      if (!minmaxUtils.isValid(worldMinmax)) {
        minmaxUtils.setDefaultValue(worldMinmax);
      }

      const camera = workspaceScene.camera;
      const [near, far] = minmaxUtils.calcNearFarByMinMax(worldMinmax, camera);
      camera.updateNearFar(near, far);
      this.renderGraph.requestFrame();
    };

    this.getMinMax = () => {
      return worldMinmax;
    };

    this._cameraMoveHandler = (camera) => {
      const workspaceScene = this.renderGraph.getWorkspaceScene();
      const cadsAdditionalScene = this.renderGraph.getCadsAdditionalScene();
      const surfelsDepthScene = this.renderGraph.getSurfelsDepthScene();
      const surfelsAdditiveScene = this.renderGraph.getSurfelsAdditiveScene();
      const postBlendingWorkspaceScene = this.renderGraph.getPostBlendingScene();
      const interfaceScene = this.renderGraph.getInterfaceScene();
      const postBlendingInterfaceScene = this.renderGraph.getPostBlendingInterfaceScene();
      const workspaceCamera = workspaceScene.camera;
      if (camera === workspaceCamera) {
        if (this.cameraControl instanceof TrackballControl) {
          this.cameraControl.updateRotationIndicator();
        }
        this._updateNearFar();
        workspaceScene.updateLights(config.lights);
        surfelsDepthScene.updateLights(config.lights);
        surfelsAdditiveScene.updateLights(config.lights);
        postBlendingWorkspaceScene.updateLights(config.lights);
        interfaceScene.updateLights(config.widgetsLights);
        postBlendingInterfaceScene.updateLights(config.widgetsLights);
        cadsAdditionalScene.updateLights(config.widgetsLights);
        this.renderGraph.getPass(renderGraph.COMPARE_DRAW).getScenes()[0].updateLights(config.lights);
        this.renderGraph.getPass(renderGraph.COMPARE_DRAW).getScenes()[1].updateLights(config.lights);
      }
    };

    this._objectsBboxUpdateHandler = (objects) => {
      for (let object of objects) {
        if (this.objects.includes(object)) {
          this._updateNearFar();
          break;
        }
      }
    };

  }

  _initEvents() {

    events.on("client_mousemove", this._mouseMoveHandler);

    events.on("control_zoom", this._zoomHandler);
    events.on("control_zoom_end", this._zoomEndHandler);
    events.on("control_start", this._controlStartHandler);
    events.on("control_move", this._controlRotateHandler);
    events.on("control_pan", this._controlPanHandler);
    events.on("control_end", this._controlEndHandler);
    events.on("control_set_target", this._controlResetPointHandler);

    events.on("camera_move", this._cameraMoveHandler);
    events.on("object_bboxes_updated", this._objectsBboxUpdateHandler);
    events.on("object_visibility_change", this._updateNearFar);

  }

  _deInitEvents() {

    events.off("client_mousemove", this._mouseMoveHandler);

    events.off("control_zoom", this._zoomHandler);
    events.off("control_zoom_end", this._zoomEndHandler);
    events.off("control_start", this._controlStartHandler);
    events.off("control_move", this._controlRotateHandler);
    events.off("control_pan", this._controlPanHandler);
    events.off("control_end", this._controlEndHandler);
    events.off("control_set_target", this._controlResetPointHandler);

    events.off("camera_move", this._cameraMoveHandler);
    events.off("object_bboxes_updated", this._objectsBboxUpdateHandler);
    events.off("object_visibility_change", this._updateNearFar);

  }

  _setActive(active) {
    this._active = active;
  }

  _isActive() {
    return this._active;
  }

  isInViewport(x, y) {
    return this.isInViewportXY(x, y);
  }

  isInViewportXY(windowX, windowY) {
    return windowX >= this._viewport[0] && windowX <= this._viewport[0] + this._viewport[2] &&
        windowY >= this._viewport[1] && windowY <= this._viewport[1] + this._viewport[3];
  }

  async getPixelWorldPoint(clientX, clientY, camera, dest, forcedRaycast = false, forcedRead = false) {

    if (appState.getState(appState.GS_DRAW_DEPTH) && compat.mrtEnabled && !forcedRaycast) {
      const textureX = this.toViewportX(clientX);
      const textureY = this.toViewportY(clientY);

      const pixelDepth = forcedRead ? await this.bufferReader.readPixelGPU(textureX, textureY, BufferReaderType.GENERAL_PURPOSE) :
                                      await this.bufferReader.readPixelCache(textureX, textureY, BufferReaderType.GENERAL_PURPOSE);

      if (pixelDepth < 0.0) {
        return null;
      }

      this.bufferReader.depthToWorldSpace(pixelDepth, clientX, clientY, this.getViewport(), camera, dest);
      const pickID = await this.bufferReader.readPixelCache(textureX, textureY, BufferReaderType.OBJECT_ID);
      const object = pickID ? workspace.getObjectByID(pickID) : null;

      return {object};
    } else {
      const objects = this.objects.filter(object => workspace.checkIsReady(object) && object.getVisibility());
      const drawableToObject = new Map();
      for (const object of objects) {
        for (const drawable of object.getAllLoadedDrawables()) {
          drawableToObject.set(drawable, object);
        }
      }
      const {x, y} = screenUtils.toScreen(this.getViewport(), camera.viewport, clientX, clientY);
      const res = selector.findClosestIntersection(dest, x, y, camera, drawableToObject.keys());
      if (!res) {
        return null;
      }

      const object = drawableToObject.get(res);
      return {object};
    }

  }

  setBackgroundColor(r,g,b,a) {
    this.renderGraph.getPass(renderGraphManager.BACKGROUND).getMainScene().setClearColor(r,g,b,a);
  }

  setBackgroundTexture(texture) {
    this.renderGraph.getPass(renderGraphManager.BACKGROUND).setTexture(texture);
  }

  cleanup() {
    this._deInitEvents();
    this.renderGraph.cleanup();
    this.bufferReader.cleanup();

    this.objects.length = 0;

  }

}

function _updateResolvePass() {
  const graphsData = [];
  for (let window of _windows.values()) {
    graphsData.push({
      viewport: window.getViewport(),
      colorTexture: window.renderGraph.getOutputTexture(),
    });
  }
  _resolvePass.update(graphsData);
}

export default {
  init() {
    _windowID = 0;
    _resolvePass = new ResolvePass();
  },

  createWindow(viewport, camType) {
    _windows.set(_windowID, new Window(viewport, camType));
    _updateResolvePass();
    return _windowID++;
  },

  deleteWindow(windowID) {
    if (windowID === 0) {
      output.error(output.API_ERROR, "Can't delete main window");
      return;
    }

    const window = _windows.get(windowID);
    if (!window) {
      output.error(output.API_ERROR, "Can't delete non-existent window with id: \"" + windowID + "\"");
      return;
    }

    window.cleanup();
    _windows.delete(windowID);
    _updateResolvePass();
  },

  setWindowObjects(objects, windowID) {
    const window = _windows.get(windowID);
    if (!window) {
      output.error(output.API_ERROR, "Can't set object to non-existent window with id: \"" + windowID + "\"");
      return;
    }

    for (const object of window.objects) {
      window.removeObject(object);
    }

    window.objects = objects;
    window.updateObjectsScene();

    window.cameraControl.setDefaultPosition(workspace.getBBox(object => objects.includes(object)));
    window.renderGraph.requestFrame();
    window.bufferReader.dropBuffers();
  },

  updateWindowViewport(windowID, viewport) {
    const window = _windows.get(windowID);
    if (!window) {
      output.error(output.API_ERROR, "Can't set viewport to non-existent window with id: \"" + windowID + "\"");
      return;
    }

    window.setViewport(viewport);
    window.cameraControl.setDefaultPosition(workspace.getBBox(object => window.objects.includes(object)));
    window.renderGraph.requestFrame();
    _updateResolvePass();
    window.bufferReader.dropBuffers();
  },

  getWindow(id) {
    return _windows.get(id);
  },

  update(dt) {
    const gpuTimeDebugEnabled = appState.getStateValue("GPUTimeDebugEnabled");
    for (const window of _windows.values()) {
      if (window.cameraControl) {
        window.cameraControl.update(dt);
      }
    }

    const windowsToUpdate = Array.from(_windows.values()).filter(window => window.renderGraph.isFrameRequested());
    if (windowsToUpdate.length === 0) {
      return;
    }

    if (gpuTimeDebugEnabled) {
      renderer.startTimeQuery();
    }

    for (const window of windowsToUpdate) {
      window.renderGraph.render(dt);
    }
    _resolvePass.render();

    if (gpuTimeDebugEnabled) {
      renderer.stopTimeQuery();
      const elapsedNanos = renderer.getTimeQueryResult();
      if (elapsedNanos) {
        const averagedNanos = getAveragedGPUTime(elapsedNanos);
        console.log("avg frame time (ms):", averagedNanos / 1000000);
      }
    }

  },

  updateWindows() {
    for (let window of _windows.values()) {
      window.renderGraph.requestFrame();
    }
  },

  resizeWindows(width, height) {
    width = Math.max(width, 1);
    height = Math.max(height, 1);

    const wAspect = width / _prevWidth;
    const hAspect = height / _prevHeight;

    let scene = 0;
    for (let window of _windows.values()) {
      const viewport = window.getViewport();
      viewport[0] = Math.round(viewport[0] * wAspect);
      viewport[1] = Math.round(viewport[1] * hAspect);
      viewport[2] = Math.round(viewport[2] * wAspect);
      viewport[3] = Math.round(viewport[3] * hAspect);

      window.setViewport(viewport);
      _resolvePass.scenes[scene++].camera.setViewport(viewport[2], viewport[3], viewport[0], viewport[1]);
      window.renderGraph.requestFrame();
      window.bufferReader.dropBuffers();
    }

    _prevWidth = width;
    _prevHeight = height;
  },

  disableControls() {
    for (let window of _windows.values()) {
      window.cameraControl.setEnabled(false);
    }
  },

  enableControls() {
    for (let window of _windows.values()) {
      window.cameraControl.setEnabled(true);
    }
  },

  cleanup() {
    for (let window of _windows.values()) {
      window.cleanup();
    }

    _windows.clear();
    _windowID = 0;
    _resolvePass = null;

    renderGraphManager.cleanup();
    _GPUTimeMeasurements.length = 0;
  },

  applyToAllWindows(func) {
    for (const window of _windows.values()) {
      func(window);
    }
  },

  MAIN,
  ALIGNEE,
  ALIGNER,
}

function getAveragedGPUTime(newMeasurement) {
  const period = 400;

  if (_GPUTimeMeasurements.length === period) {
    _GPUTimeMeasurements.shift();
  }
  _GPUTimeMeasurements.push(newMeasurement);

  const sum = _GPUTimeMeasurements.reduce((a, b) => a + b, 0);
  const avg = (sum / _GPUTimeMeasurements.length) || 0;

  return avg;
}
